import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Container, 
  Typography, 
  Grid, 
  Button, 
  Box, 
  Card, 
  CardContent,
  Paper,
  Avatar,
  Chip
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import EnvironmentSwitcher from "../components/EnvironmentSwitcher";
import {
  Assignment as BitacorasIcon,
  Receipt as FacturaIcon,
  Category as ConceptosIcon,
  CheckCircle as EstatusIcon,
  TypeSpecimen as TiposIcon,
  Person as PersonIcon,
  NoteAdd as AdendaIcon,
} from "@mui/icons-material";

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();

  const options = [
    { 
      label: "Bitácoras", 
      path: "/bitacoras", 
      icon: <BitacorasIcon />,
      description: "Gestiona las bitácoras del sistema",
      color: "#2196F3"
    },
    { 
      label: "Ingreso de Factura", 
      path: "/ingreso-factura", 
      icon: <FacturaIcon />,
      description: "Registra nuevas facturas",
      color: "#4CAF50"
    },
    { 
      label: "Adenda Bitácora", 
      path: "/adenda-bitacora", 
      icon: <AdendaIcon />,
      description: "Crea y gestiona adendas para bitácoras",
      color: "#00BCD4"
    },
    { 
      label: "Conceptos de Gasto", 
      path: "/conceptos-gasto", 
      icon: <ConceptosIcon />,
      description: "Administra conceptos de gasto",
      color: "#FF9800"
    },
    { 
      label: "Estatus de Factura", 
      path: "/estatus-factura", 
      icon: <EstatusIcon />,
      description: "Controla el estatus de facturas",
      color: "#9C27B0"
    },
    { 
      label: "Tipos de Factura", 
      path: "/tipos-factura", 
      icon: <TiposIcon />,
      description: "Configura tipos de factura",
      color: "#F44336"
    },
  ];

  return (
    <Container maxWidth="lg">
      {/* Welcome Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, #00AEEF 0%, #0078d4 100%)',
          color: 'white',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mr: 2,
              background: 'rgba(255, 255, 255, 0.2)',
            }}
          >
            <PersonIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              ¡Bienvenido, {user?.name || "Usuario"}!
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Sistema de Gestión de Facturación - Cryo
            </Typography>
          </Box>
        </Box>
        <Chip 
          label="Sesión Activa" 
          color="success" 
          sx={{ 
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      </Paper>

      {/* Environment Configuration */}

      {process.env.REACT_APP_ENVIRONMENT === "PREPROD" &&
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
              ⚙️ Configuración de Ambiente
            </Typography>
            <EnvironmentSwitcher showDetails={true} />
          </CardContent>
        </Card>
      }

      {/* Quick Actions */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        🚀 Accesos Rápidos
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Selecciona una opción para acceder a las funcionalidades del sistema
      </Typography>

      <Grid container spacing={3}>
        {options.map((option) => (
          <Grid item xs={12} sm={6} md={4} key={option.path}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: 3,
                boxShadow: 2,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => navigate(option.path)}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    mx: 'auto',
                    mb: 2,
                    background: option.color,
                    boxShadow: `0 4px 14px 0 ${option.color}40`,
                  }}
                >
                  {option.icon}
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {option.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {option.description}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    background: option.color,
                    '&:hover': {
                      background: option.color,
                      opacity: 0.9,
                    },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                  }}
                >
                  Acceder
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Footer */}
      <Box textAlign="center" mt={6} py={3}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Logística y Transporte Lyta - Todos los derechos reservados
        </Typography>
      </Box>
    </Container>
  );
};

export default Home;
